import { Link } from "react-router-dom";

/**
 * School Smiles Australia logo.
 *
 * The van artwork is a rasterised PNG (/logo.png), so we overlay two small
 * SVG "wheels" positioned to sit roughly over the printed wheels in the
 * illustration. They spin briefly every few seconds via the `wheel-drive`
 * keyframe (see index.css) and continuously while the logo is hovered.
 *
 * If the wheels look slightly off, nudge the top/left values in
 * WHEEL_POSITIONS below (they are percentages of the crop container).
 */
/**
 * Wheel positions inside the cropped logo container.
 * The container shows the top ~55% of the source image (aspect-[20/11]).
 * Van wheel centres in the source image sit at ~(27.5%, 47%) and (71%, 47%)
 * of the full square. Translated to the cropped container:
 *   x stays the same;  y = 47% / 55% ≈ 85.5% of container height.
 */
const WHEEL_POSITIONS = [
  { left: "27.5%", top: "85.5%" }, // rear wheel
  { left: "71%",   top: "85.5%" }, // front wheel
];

const Wheel = ({ left, top, delay = "0s" }) => (
  <span
    className="absolute z-10 pointer-events-none wheel-drive"
    style={{
      left,
      top,
      width: "6.5%",
      aspectRatio: "1 / 1",
      transform: "translate(-50%, -50%)",
      animationDelay: delay,
    }}
    aria-hidden="true"
  >
    <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-sm">
      <circle cx="12" cy="12" r="11" fill="#0F2A45" />
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="#FFFFFF" strokeWidth="0.6" opacity="0.55" />
      <line x1="12" y1="3" x2="12" y2="21" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" opacity="0.95" />
      <line x1="3" y1="12" x2="21" y2="12" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" opacity="0.95" />
      <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" stroke="#FFFFFF" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <line x1="18.5" y1="5.5" x2="5.5" y2="18.5" stroke="#FFFFFF" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <circle cx="12" cy="12" r="2.2" fill="#FFD45C" />
    </svg>
  </span>
);

export const Logo = ({ variant = "header" }) => {
  const iconWrap =
    variant === "footer"
      ? "h-16 md:h-20 aspect-[20/11]"
      : "h-12 md:h-14 aspect-[20/11]";
  const wordmark =
    variant === "footer" ? "text-xl md:text-2xl" : "text-lg md:text-xl";

  return (
    <Link
      to="/"
      className="flex items-center gap-3 group"
      data-testid="site-logo"
      aria-label="School Smiles Australia — home"
    >
      {/* Cropped van illustration + spinning wheel overlays */}
      <span className={`relative ${iconWrap} overflow-hidden shrink-0`} aria-hidden="true">
        <img
          src="/logo.png"
          alt=""
          loading="eager"
          className="absolute top-0 left-0 w-full h-auto object-contain transition-transform duration-200 group-hover:scale-[1.03]"
        />
        {WHEEL_POSITIONS.map((pos, i) => (
          <Wheel key={i} left={pos.left} top={pos.top} delay={`${i * 0.15}s`} />
        ))}
      </span>
      <span className="flex flex-col leading-tight">
        <span className={`font-display font-bold text-[#14324A] ${wordmark} tracking-tight`}>
          School Smiles Australia
        </span>
        <span className="text-[10px] md:text-xs text-[#4B6B86] font-medium tracking-wide">
          Mobile dental care for brighter futures
        </span>
      </span>
    </Link>
  );
};

export default Logo;
